import React, { useEffect, useState } from "react";
import { Download, Trash2, Search, Filter, ExternalLink, X } from "lucide-react";
import { toast } from "sonner";
import { admin, errText, STATUS_OPTIONS, statusLabel, fmtDate } from "../../lib/adminApi";
import { CAREER_TYPES } from "../../data/site";

const badge = {
  new: "bg-brand-orange/20 text-brand-orange", under_review: "bg-sky-500/20 text-sky-300", shortlisted: "bg-emerald-500/20 text-emerald-300",
  interview: "bg-violet-500/20 text-violet-300", selected: "bg-green-500/20 text-green-300", on_hold: "bg-amber-500/20 text-amber-300",
  rejected: "bg-red-500/20 text-red-300", talent_pool: "bg-slate-500/20 text-slate-300",
};

const sel = "bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-orange [&>option]:bg-[#0A1F44]";

export const CandidatesPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [active, setActive] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await admin.get("/careers/applications", { params: { q: q || undefined, status: status || undefined, application_type: type || undefined } });
      setItems(data.items);
    } catch (e) {
      toast.error(errText(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [status, type]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = async (id, s) => {
    try {
      await admin.patch(`/careers/applications/${id}/status`, null, { params: { status: s } });
      setItems((xs) => xs.map((x) => (x.id === id ? { ...x, status: s } : x)));
      if (active?.id === id) setActive((a) => ({ ...a, status: s }));
      toast.success(`Status set to ${statusLabel(s)}`);
    } catch (e) {
      toast.error(errText(e?.response?.data?.detail));
    }
  };

  const download = async (c) => {
    try {
      const res = await admin.get(`/careers/applications/${c.id}/resume`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${c.full_name.replace(/[^\w-]+/g, "_")}_resume.${c.resume?.ext || "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download resume");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.full_name}'s profile and resume? This cannot be undone.`)) return;
    try {
      await admin.delete(`/careers/applications/${c.id}`);
      setItems((xs) => xs.filter((x) => x.id !== c.id));
      setActive(null);
      toast.success("Candidate deleted");
    } catch (e) {
      toast.error(errText(e?.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-candidates">
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex-1 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input data-testid="admin-candidates-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, skills, city…" className="w-full bg-white/[0.04] border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-brand-orange" />
        </form>
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-slate-500" />
          <select data-testid="admin-candidates-status" value={status} onChange={(e) => setStatus(e.target.value)} className={sel}>
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <select data-testid="admin-candidates-type" value={type} onChange={(e) => setType(e.target.value)} className={sel}>
            <option value="">All types</option>
            {CAREER_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
      </div>

      <div className="glass rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[880px]">
            <thead>
              <tr className="text-left font-mono text-[10px] tracking-wider uppercase text-slate-500 border-b border-white/10">
                <th className="px-4 py-3">Candidate</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Interest</th><th className="px-4 py-3">Location</th><th className="px-4 py-3">Resume</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Submitted</th><th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8} data-testid="admin-candidates-empty" className="px-4 py-10 text-center text-slate-500">No candidates match.</td></tr>
              ) : items.map((c) => (
                <tr key={c.id} data-testid={`admin-candidate-row-${c.id}`} className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer" onClick={() => setActive(c)}>
                  <td className="px-4 py-3"><p className="text-white font-medium">{c.full_name}</p><p className="text-slate-500 text-xs">{c.email}{c.application_number > 1 ? ` · #${c.application_number}` : ""}</p></td>
                  <td className="px-4 py-3 text-slate-300">{c.application_type_label.split(" / ")[0]}</td>
                  <td className="px-4 py-3 text-slate-300">{c.area_of_interest}{c.opening_title && <span className="block text-xs text-brand-orange">{c.opening_title}</span>}</td>
                  <td className="px-4 py-3 text-slate-300">{c.city}</td>
                  <td className="px-4 py-3">{c.resume && !c.resume.is_deleted ? <button onClick={(e) => { e.stopPropagation(); download(c); }} data-testid={`admin-candidate-resume-${c.id}`} className="inline-flex items-center gap-1 text-brand-orange hover:underline"><Download size={14} /> {c.resume.ext.toUpperCase()}</button> : <span className="text-slate-600">—</span>}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <select data-testid={`admin-candidate-status-${c.id}`} value={c.status} onChange={(e) => updateStatus(c.id, e.target.value)} className={`rounded-full px-3 py-1 text-xs font-medium border-0 focus:outline-none ${badge[c.status] || badge.talent_pool} [&>option]:bg-[#0A1F44] [&>option]:text-white`}>
                      {STATUS_OPTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">{fmtDate(c.created_at)}</td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}><button onClick={() => remove(c)} data-testid={`admin-candidate-delete-${c.id}`} aria-label="Delete candidate" className="text-slate-500 hover:text-red-400 p-1"><Trash2 size={15} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {active && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm" onClick={() => setActive(null)}>
          <aside data-testid="admin-candidate-drawer" onClick={(e) => e.stopPropagation()} className="w-full max-w-lg h-full overflow-y-auto bg-navy-deep border-l border-white/10 p-6 md:p-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-head text-2xl text-white font-semibold">{active.full_name}</h3>
                <p className="text-slate-400 text-sm">{active.application_type_label}</p>
              </div>
              <button onClick={() => setActive(null)} aria-label="Close" data-testid="admin-candidate-drawer-close" className="text-slate-400 hover:text-white p-1"><X size={20} /></button>
            </div>
            <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
              {[["Email", <a href={`mailto:${active.email}`} className="text-brand-orange hover:underline break-all">{active.email}</a>], ["Phone", <a href={`tel:${active.phone}`} className="text-brand-orange hover:underline">{active.phone}</a>], ["Location", active.city], ["Area of interest", active.area_of_interest], ["Applying for", active.opening_title || "General profile"], ["Qualification", active.qualification],
                ...(active.application_type === "experienced" ? [["Company", active.current_company], ["Job title", active.job_title], ["Experience", active.experience]] : [["College", active.college], ["Year", active.graduation_year]]),
                ["Submitted", fmtDate(active.created_at)], ["Consent", active.consent ? `Given · ${fmtDate(active.consent_at)}` : "—"]].map(([k, v]) => (
                <div key={k}><dt className="font-mono text-[10px] tracking-wider uppercase text-slate-500">{k}</dt><dd className="text-slate-200 mt-1">{v || "—"}</dd></div>
              ))}
            </dl>
            <div className="mt-5"><p className="font-mono text-[10px] tracking-wider uppercase text-slate-500">Skills</p><p className="text-slate-200 mt-1 text-sm">{active.skills}</p></div>
            {(active.linkedin_url || active.portfolio_url) && (
              <div className="mt-5 flex flex-wrap gap-3 text-sm">
                {active.linkedin_url && <a href={active.linkedin_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-brand-orange hover:underline">LinkedIn <ExternalLink size={12} /></a>}
                {active.portfolio_url && <a href={active.portfolio_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-brand-orange hover:underline">Portfolio / GitHub <ExternalLink size={12} /></a>}
              </div>
            )}
            {active.message && <div className="mt-5"><p className="font-mono text-[10px] tracking-wider uppercase text-slate-500">Message</p><p className="text-slate-300 mt-1 text-sm leading-relaxed whitespace-pre-wrap">{active.message}</p></div>}
            <div className="mt-8 pt-6 border-t border-white/10 flex flex-wrap items-center gap-3">
              <select value={active.status} onChange={(e) => updateStatus(active.id, e.target.value)} data-testid="admin-drawer-status" className={sel}>
                {STATUS_OPTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              {active.resume && !active.resume.is_deleted && <button onClick={() => download(active)} data-testid="admin-drawer-resume" className="btn-secondary !py-2 !px-4 text-sm"><Download size={14} /> Resume ({Math.round(active.resume.size / 1024)} KB)</button>}
              <button onClick={() => remove(active)} data-testid="admin-drawer-delete" className="ml-auto inline-flex items-center gap-2 text-sm text-red-400 hover:text-red-300"><Trash2 size={14} /> Delete</button>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default CandidatesPanel;
